import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Combobox } from "@/components/ui/combobox";
import { api, formatApiError, todayISO } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

const PAYMENT_METHODS = ["Bank Transfer", "Cash", "Check", "Credit/Debit Card", "Online", "Other"];
const ORIGINS = [
  { value: "sunday-service", label: "Sunday Service" },
  { value: "online", label: "Online Giving" },
  { value: "mail", label: "Mail-in" },
  { value: "special-event", label: "Special Event" },
  { value: "in-person", label: "In-person" },
  { value: "other", label: "Other" },
];
const FREQS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

// -------- Donation Dialog (income) --------
function DonationForm({ orgId, donors: initialDonors, funds: initialFunds, categories: initialCategories, origins: initialOrigins = [], onSaved, onClose, txn, onLookupsChanged }) {
  const isEdit = !!txn; // editing existing single income transaction
  const [donors, setDonors] = useState(initialDonors);
  const [funds, setFunds] = useState(initialFunds);
  const [categories, setCategories] = useState(initialCategories);
  const [origins, setOrigins] = useState(initialOrigins);
  const [donorId, setDonorId] = useState(txn?.donor_id || "");
  const [date, setDate] = useState(txn?.date || todayISO());
  const [paymentMethod, setPaymentMethod] = useState(txn?.payment_method || "Bank Transfer");
  const [origin, setOrigin] = useState(txn?.origin || "");
  const [notes, setNotes] = useState(txn?.description || "");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFreq, setRecurringFreq] = useState("monthly");
  const [taxDeductible, setTaxDeductible] = useState(txn?.tax_deductible !== false);
  const [lines, setLines] = useState(
    isEdit
      ? [{ fund_id: txn.fund_id || "", category_id: txn.category_id || "", amount: String(txn.amount), fee: String(txn.fee || 0) }]
      : [{ fund_id: "", category_id: "", amount: "", fee: "" }],
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setDonors(initialDonors); }, [initialDonors]);
  useEffect(() => { setFunds(initialFunds); }, [initialFunds]);
  useEffect(() => { setCategories(initialCategories); }, [initialCategories]);
  useEffect(() => { setOrigins(initialOrigins); }, [initialOrigins]);

  const createOriginInline = async (name) => {
    try {
      const { data } = await api.post(`/organizations/${orgId}/donation-origins`, { name: name.trim() });
      const newO = { id: data.id, name: data.name };
      setOrigins((arr) => [...arr, newO]);
      onLookupsChanged?.();
      toast.success(`Created origin "${newO.name}"`);
      return { value: newO.name, label: newO.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const createDonorInline = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(/\s+/);
    const first_name = parts[0];
    const last_name = parts.length > 1 ? parts[parts.length - 1] : "";
    const middle_name = parts.length > 2 ? parts.slice(1, -1).join(" ") : "";
    try {
      const { data } = await api.post(`/organizations/${orgId}/donors`, {
        first_name, middle_name, last_name, active: true,
      });
      const newDonor = { id: data.id, name: data.name || trimmed, active: true };
      setDonors((arr) => [...arr, newDonor]);
      onLookupsChanged?.();
      toast.success(`Created donor "${newDonor.name}"`);
      return { value: newDonor.id, label: newDonor.name };
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  const createFundInline = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    try {
      const { data } = await api.post(`/organizations/${orgId}/funds`, { name: trimmed });
      const newFund = { id: data.id, name: data.name };
      setFunds((arr) => [...arr, newFund]);
      onLookupsChanged?.();
      toast.success(`Created fund "${newFund.name}"`);
      return { value: newFund.id, label: newFund.name };
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  const createCategoryInline = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    try {
      const { data } = await api.post(`/organizations/${orgId}/categories`, { name: trimmed, type: "income" });
      const newCat = { id: data.id, name: data.name, type: "income" };
      setCategories((arr) => [...arr, newCat]);
      onLookupsChanged?.();
      toast.success(`Created income category "${newCat.name}"`);
      return { value: newCat.id, label: newCat.name };
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  const incomeCategories = useMemo(() => categories.filter((c) => c.type === "income"), [categories]);

  const updateLine = (idx, key, val) => {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));
  };
  const addLine = () => setLines((ls) => [...ls, { fund_id: "", category_id: "", amount: "", fee: "" }]);
  const removeLine = (idx) => setLines((ls) => ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls);

  // Online itemised fees (Stripe, partner share, loan repayment, etc.)
  const [onlineFees, setOnlineFees] = useState(() => (txn?.online_fees && Array.isArray(txn.online_fees) && txn.online_fees.length > 0)
    ? txn.online_fees.map((f) => ({ description: f.description || "", amount: String(f.amount ?? "") }))
    : []);
  const addOnlineFee = () => setOnlineFees((arr) => [...arr, { description: "", amount: "" }]);
  const updateOnlineFee = (i, k, v) => setOnlineFees((arr) => arr.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  const removeOnlineFee = (i) => setOnlineFees((arr) => arr.filter((_, idx) => idx !== i));
  const onlineFeesTotal = onlineFees.reduce((s, f) => s + (Number(f.amount) || 0), 0);
  const showOnlineFees = paymentMethod === "Online";

  const totalAmount = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const totalFee = lines.reduce((s, l) => s + (Number(l.fee) || 0), 0);

  const submit = async () => {
    if (!donorId) return toast.error("Please select a donor");
    if (!date) return toast.error("Date required");
    for (const l of lines) {
      if (!l.fund_id) return toast.error("Each line needs a fund");
      if (!l.category_id) return toast.error("Each line needs a category");
      if (!l.amount || Number(l.amount) <= 0) return toast.error("Each amount must be positive");
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        // single-line edit only — use PATCH on the original transaction
        const l = lines[0];
        await api.patch(`/organizations/${orgId}/transactions/${txn.id}`, {
          date,
          type: "income",
          amount: Number(l.amount),
          category_id: l.category_id,
          description: notes,
          donor_id: donorId,
          payment_method: paymentMethod || null,
          reference: txn.reference || null,
          fund_id: l.fund_id,
          fee: Number(l.fee) || 0,
          origin: origin || null,
          tax_deductible: taxDeductible,
        });
        toast.success("Donation updated");
      } else {
        await api.post(`/organizations/${orgId}/donations`, {
          donor_id: donorId,
          date,
          payment_method: paymentMethod || null,
          origin: origin || null,
          notes,
          tax_deductible: taxDeductible,
          is_recurring: isRecurring,
          recurring_frequency: recurringFreq,
          online_fees: showOnlineFees ? onlineFees.filter((f) => Number(f.amount) > 0).map((f) => ({ description: (f.description || "Processing fee").trim(), amount: Number(f.amount) })) : null,
          lines: lines.map((l) => ({
            fund_id: l.fund_id,
            category_id: l.category_id,
            amount: Number(l.amount),
            fee: Number(l.fee) || 0,
          })),
        });
        toast.success(lines.length > 1 ? `Donation recorded across ${lines.length} funds` : "Donation recorded");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 py-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Donor <span className="text-destructive">*</span></Label>
          <Combobox
            value={donorId}
            onValueChange={setDonorId}
            items={donors.filter((d) => d.active !== false).map((d) => ({ value: d.id, label: d.name }))}
            placeholder="Select or type to create a donor…"
            searchPlaceholder="Search donors or type a new name…"
            emptyMessage="No donors. Type a name to create one."
            onCreate={createDonorInline}
            testid="donation-donor-select"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Date <span className="text-destructive">*</span></Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="donation-date-input" className="rounded-xl" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Payment Method</Label>
          <Select value={paymentMethod} onValueChange={setPaymentMethod}>
            <SelectTrigger data-testid="donation-payment-method-select" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Origin of Donation</Label>
          <Combobox
            value={origin}
            onValueChange={setOrigin}
            items={origins.map((o) => ({ value: o.name, label: o.name }))}
            placeholder="Select or create origin…"
            searchPlaceholder="Search or type a new origin…"
            emptyMessage="No matching origin."
            onCreate={createOriginInline}
            testid="donation-origin-select"
          />
        </div>
      </div>

      {/* Fund lines */}
      <div className="rounded-2xl border border-border overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-secondary/60 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <div className="col-span-4">Fund <span className="text-destructive normal-case tracking-normal">*</span></div>
          <div className="col-span-3">Category</div>
          <div className="col-span-2">Amount <span className="text-destructive normal-case tracking-normal">*</span></div>
          <div className="col-span-2">Fee</div>
          <div className="col-span-1"></div>
        </div>
        <div className="divide-y divide-border">
          {lines.map((l, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center">
              <div className="col-span-4">
                <Combobox
                  value={l.fund_id}
                  onValueChange={(v) => updateLine(idx, "fund_id", v)}
                  items={funds.map((f) => ({ value: f.id, label: f.name }))}
                  placeholder="Select or create fund…"
                  searchPlaceholder="Search funds…"
                  emptyMessage="No funds. Type a name to create one."
                  onCreate={createFundInline}
                  testid={`donation-fund-select-${idx}`}
                />
              </div>
              <div className="col-span-3">
                <Combobox
                  value={l.category_id}
                  onValueChange={(v) => updateLine(idx, "category_id", v)}
                  items={incomeCategories.map((c) => ({ value: c.id, label: c.name }))}
                  placeholder="Select category…"
                  searchPlaceholder="Search income categories…"
                  emptyMessage="No matching category."
                  onCreate={createCategoryInline}
                  testid={`donation-category-select-${idx}`}
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={l.amount}
                  onChange={(e) => updateLine(idx, "amount", e.target.value)}
                  data-testid={`donation-amount-input-${idx}`}
                  className="rounded-xl h-10 tabular-nums"
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={l.fee}
                  onChange={(e) => updateLine(idx, "fee", e.target.value)}
                  data-testid={`donation-fee-input-${idx}`}
                  className="rounded-xl h-10 tabular-nums"
                />
              </div>
              <div className="col-span-1 flex justify-end">
                {!isEdit && lines.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-lg text-destructive hover:bg-destructive/10"
                    onClick={() => removeLine(idx)}
                    data-testid={`donation-remove-line-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        {!isEdit && (
          <button
            type="button"
            onClick={addLine}
            data-testid="donation-add-line-button"
            className="w-full flex items-center justify-center gap-1.5 px-4 py-3 text-sm text-primary hover:bg-primary/5 border-t border-border transition-colors"
          >
            <Plus className="w-4 h-4" /> Add another fund
          </button>
        )}

        {showOnlineFees && !isEdit && (
          <div className="mt-4 rounded-2xl border border-primary/20 bg-primary/5 p-4" data-testid="online-fees-section">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-serif text-lg">Online fees breakdown</div>
                <p className="text-[11px] text-muted-foreground mt-0.5">Each itemised fee is recorded as its own linked expense and subtracted from the deposit. The donor's profile still shows the full gross gift.</p>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase text-muted-foreground">Total fees</div>
                <div className="font-mono tabular-nums text-base">{formatUSD(onlineFeesTotal)}</div>
              </div>
            </div>
            <div className="space-y-2 mt-3">
              {onlineFees.map((f, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`online-fee-row-${i}`}>
                  <Input
                    className="col-span-7 rounded-xl h-10"
                    placeholder="Description (e.g. Stripe Processing Fee)"
                    value={f.description}
                    onChange={(e) => updateOnlineFee(i, "description", e.target.value)}
                    data-testid={`online-fee-desc-${i}`}
                  />
                  <Input
                    className="col-span-4 rounded-xl h-10 tabular-nums"
                    type="number" step="0.01" min="0"
                    placeholder="0.00"
                    value={f.amount}
                    onChange={(e) => updateOnlineFee(i, "amount", e.target.value)}
                    data-testid={`online-fee-amt-${i}`}
                  />
                  <Button type="button" size="icon" variant="ghost" className="col-span-1 rounded-xl text-destructive" onClick={() => removeOnlineFee(i)} data-testid={`online-fee-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" className="w-full rounded-xl h-10" onClick={addOnlineFee} data-testid="online-fee-add">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add fee
              </Button>
            </div>
            <div className="mt-3 pt-3 border-t border-primary/20 grid grid-cols-3 gap-2 text-xs">
              <div><div className="text-[10px] text-muted-foreground">Gross (donor credit)</div><div className="font-mono tabular-nums">{formatUSD(totalAmount)}</div></div>
              <div><div className="text-[10px] text-muted-foreground">Fees</div><div className="font-mono tabular-nums text-destructive">-{formatUSD(onlineFeesTotal)}</div></div>
              <div><div className="text-[10px] text-muted-foreground">Net deposit (ledger)</div><div className="font-mono tabular-nums text-primary">{formatUSD(Math.max(0, totalAmount - onlineFeesTotal))}</div></div>
            </div>
          </div>
        )}
        {lines.length > 1 && (
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-secondary/40 text-sm border-t border-border">
            <div className="col-span-7 font-medium text-muted-foreground">Total</div>
            <div className="col-span-2 font-mono tabular-nums font-medium">${totalAmount.toFixed(2)}</div>
            <div className="col-span-2 font-mono tabular-nums text-muted-foreground">${totalFee.toFixed(2)}</div>
            <div className="col-span-1"></div>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>Notes</Label>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" data-testid="donation-notes-input" className="rounded-xl" />
      </div>

      <div className="flex flex-wrap items-center gap-6 pt-2">
        {!isEdit && (
          <>
            <div className="flex items-center gap-3">
              <Switch checked={isRecurring} onCheckedChange={setIsRecurring} data-testid="donation-recurring-switch" />
              <span className="text-sm font-medium">Recurring Donation</span>
            </div>
            {isRecurring && (
              <Select value={recurringFreq} onValueChange={setRecurringFreq}>
                <SelectTrigger className="rounded-xl h-9 w-36" data-testid="donation-recurring-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </>
        )}
        <div className="flex items-center gap-3">
          <Switch checked={taxDeductible} onCheckedChange={setTaxDeductible} data-testid="donation-tax-deductible-switch" />
          <span className="text-sm font-medium">Tax Deductible</span>
        </div>
      </div>

      <DialogFooter className="pt-2">
        <Button variant="ghost" onClick={onClose} className="rounded-xl" data-testid="donation-cancel-button">Cancel</Button>
        <Button onClick={submit} disabled={submitting} className="rounded-xl soft-glow" data-testid="donation-save-button">
          {submitting ? "Saving…" : isEdit ? "Save changes" : "Record Donation"}
        </Button>
      </DialogFooter>
    </div>
  );
}

// -------- Expense Form (simpler) --------
function ExpenseForm({ orgId, categories: initialCategories, funds: initialFunds, vendors: initialVendors, onSaved, onClose, txn, onLookupsChanged }) {
  const isEdit = !!txn;
  const [categories, setCategories] = useState(initialCategories);
  const [funds, setFunds] = useState(initialFunds);
  const [vendors, setVendors] = useState(initialVendors || []);
  const [form, setForm] = useState({
    date: txn?.date || todayISO(),
    amount: txn ? String(txn.amount) : "",
    category_id: txn?.category_id || "",
    fund_id: txn?.fund_id || "",
    vendor_id: txn?.vendor_id || "",
    description: txn?.description || "",
    payment_method: txn?.payment_method || "",
    reference: txn?.reference || "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setCategories(initialCategories); }, [initialCategories]);
  useEffect(() => { setFunds(initialFunds); }, [initialFunds]);
  useEffect(() => { setVendors(initialVendors || []); }, [initialVendors]);

  const expenseCats = categories.filter((c) => c.type === "expense");

  const createCategoryInline = async (name) => {
    try {
      const { data } = await api.post(`/organizations/${orgId}/categories`, { name: name.trim(), type: "expense" });
      const newCat = { id: data.id, name: data.name, type: "expense" };
      setCategories((arr) => [...arr, newCat]);
      onLookupsChanged?.();
      toast.success(`Created expense category "${newCat.name}"`);
      return { value: newCat.id, label: newCat.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const createFundInline = async (name) => {
    try {
      const { data } = await api.post(`/organizations/${orgId}/funds`, { name: name.trim() });
      const newFund = { id: data.id, name: data.name };
      setFunds((arr) => [...arr, newFund]);
      onLookupsChanged?.();
      toast.success(`Created fund "${newFund.name}"`);
      return { value: newFund.id, label: newFund.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const createVendorInline = async (name) => {
    try {
      const { data } = await api.post(`/organizations/${orgId}/vendors`, { name: name.trim() });
      const newV = { id: data.id, name: data.name };
      setVendors((arr) => [...arr, newV]);
      onLookupsChanged?.();
      toast.success(`Created vendor "${newV.name}"`);
      return { value: newV.id, label: newV.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const submit = async () => {
    if (!form.fund_id) return toast.error("Please choose a fund");
    if (!form.category_id) return toast.error("Please choose a category");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Amount must be positive");
    setSubmitting(true);
    try {
      const payload = {
        date: form.date,
        type: "expense",
        amount: Number(form.amount),
        category_id: form.category_id,
        description: form.description || "",
        donor_id: null,
        payment_method: form.payment_method || null,
        reference: form.reference || null,
        fund_id: form.fund_id,
        vendor_id: form.vendor_id || null,
        fee: 0,
        origin: null,
        tax_deductible: false,
      };
      if (isEdit) await api.patch(`/organizations/${orgId}/transactions/${txn.id}`, payload);
      else await api.post(`/organizations/${orgId}/transactions`, payload);
      toast.success(isEdit ? "Expense updated" : "Expense recorded");
      onSaved?.(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Fund <span className="text-destructive">*</span></Label>
          <Combobox
            value={form.fund_id}
            onValueChange={(v) => setForm({ ...form, fund_id: v })}
            items={funds.map((f) => ({ value: f.id, label: f.name }))}
            placeholder="Select or create fund…"
            searchPlaceholder="Search funds…"
            emptyMessage="No funds. Type a name to create one."
            onCreate={createFundInline}
            testid="expense-fund-select"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Category <span className="text-destructive">*</span></Label>
          <Combobox
            value={form.category_id}
            onValueChange={(v) => setForm({ ...form, category_id: v })}
            items={expenseCats.map((c) => ({ value: c.id, label: c.name }))}
            placeholder="Select or create category…"
            searchPlaceholder="Search expense categories…"
            emptyMessage="No matching category."
            onCreate={createCategoryInline}
            testid="expense-category-select"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Amount <span className="text-destructive">*</span></Label>
          <Input data-testid="expense-amount-input" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl tabular-nums" />
        </div>
        <div className="space-y-1.5">
          <Label>Date <span className="text-destructive">*</span></Label>
          <Input data-testid="expense-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Vendor / Payee</Label>
        <Combobox
          value={form.vendor_id}
          onValueChange={(v) => setForm({ ...form, vendor_id: v })}
          items={vendors.map((v) => ({ value: v.id, label: v.name }))}
          placeholder="Select or enter vendor…"
          searchPlaceholder="Search or type a new vendor…"
          emptyMessage="No matching vendor."
          onCreate={createVendorInline}
          testid="expense-vendor-select"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <Textarea data-testid="expense-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Optional notes" className="rounded-xl" />
      </div>
      <div className="space-y-1.5">
        <Label>Payment Method</Label>
        <Select value={form.payment_method || "_none"} onValueChange={(v) => setForm({ ...form, payment_method: v === "_none" ? "" : v })}>
          <SelectTrigger data-testid="expense-payment-method-select" className="rounded-xl"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">—</SelectItem>
            {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter className="pt-2">
        <Button variant="ghost" onClick={onClose} className="rounded-xl" data-testid="expense-cancel-button">Cancel</Button>
        <Button onClick={submit} disabled={submitting} className="rounded-xl soft-glow" data-testid="expense-save-button">
          {submitting ? "Saving…" : isEdit ? "Save changes" : "Record Expense"}
        </Button>
      </DialogFooter>
    </div>
  );
}

// -------- Top-level Dialog --------
export default function TransactionDialog({ open, onOpenChange, orgId, txn, categories, donors, funds, vendors, origins, defaultType = "income", onSaved, onLookupsChanged }) {
  const [type, setType] = useState(defaultType);

  useEffect(() => {
    if (txn) setType(txn.type);
    else if (open) setType(defaultType);
  }, [txn, open, defaultType]);

  const isEdit = !!txn;
  const isDonation = type === "income";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transaction-dialog" className="rounded-3xl max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">
            {isEdit ? (isDonation ? "Edit Donation" : "Edit Expense") : (isDonation ? "Record New Donation" : "Record New Expense")}
          </DialogTitle>
          <DialogDescription>
            {isDonation ? "Track a gift from a donor. Donations can be split across multiple funds." : "Log money going out of the organization."}
          </DialogDescription>
        </DialogHeader>

        {!isEdit && (
          <div className="pt-1">
            <ToggleGroup
              type="single"
              value={type}
              onValueChange={(v) => v && setType(v)}
              className="grid grid-cols-2 gap-2"
            >
              <ToggleGroupItem
                value="income"
                data-testid="txn-type-income"
                className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground border border-border h-11 rounded-xl"
              >
                Donation / Income
              </ToggleGroupItem>
              <ToggleGroupItem
                value="expense"
                data-testid="txn-type-expense"
                className="data-[state=on]:bg-expense data-[state=on]:text-expense-foreground border border-border h-11 rounded-xl"
              >
                Expense
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        )}

        {isDonation ? (
          <DonationForm
            orgId={orgId}
            donors={donors}
            funds={funds}
            categories={categories}
            origins={origins}
            onSaved={onSaved}
            onClose={() => onOpenChange(false)}
            txn={txn}
            onLookupsChanged={onLookupsChanged}
          />
        ) : (
          <ExpenseForm
            orgId={orgId}
            categories={categories}
            funds={funds}
            vendors={vendors}
            onSaved={onSaved}
            onClose={() => onOpenChange(false)}
            txn={txn}
            onLookupsChanged={onLookupsChanged}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
